<template>
  <div ref="inputFieldRef" class="input-field" :class="computedClasses">
    <div v-if="iconLeft" :key="iconLeft" class="input-field__icon">
      <i :class="iconLeft" />
    </div>
    <div class="input-field__input">
      <div class="input-field__input-label">
        <label :for="id">{{ label }}</label>
      </div>
      <div class="input-field__input-type">
        <input
          :id="props.id"
          ref="inputRef"
          :key="name"
          :type="type"
          :name="name"
          :placeholder="showPlaceholder ? placeholderUsed : ''"
          :disabled="disabled === 'true'"
          :readonly="readonly === 'true'"
          :class="{ 'input-field__input-type--focus': hasFocus }"
          :value="modelValue ?? inputValue"
          :required="required"
          @focus="focusHandler"
          @blur="blurHandler"
          @input="inputHandler"
          @keydown.enter="$emit('keyDownEnter')"
        />
      </div>
    </div>
    <button
      v-if="hasClear === 'true' && hasFocus"
      role="button"
      aria-label="Clear field"
      class="button button--text button--icon input-field__close"
      @click="
        () => {
          clearInputnFocus();
        }
      "
    >
      <i
        class="icon icon-close-filled icon--sm icon-color--grey3"
        aria-hidden="true"
      ></i>
    </button>

    <div v-if="iconRight" :key="iconRight" class="input-field__icon">
      <i :class="iconRight" />
    </div>
    <div v-if="props.error?.[name]" class="input-field__error">
      <p>{{ error[name] }}</p>
    </div>
  </div>
</template>

<script setup>
import {
  onBeforeMount,
  onUnmounted,
  ref,
  computed,
  defineProps,
  defineEmits,
  onUpdated,
  onMounted,
} from 'vue';

const props = defineProps({
  id: {
    type: String,
    default: Math.random().toString(36).substring(2, 12),
  },
  label: {
    type: String,
    default: '',
  },
  initialValue: {
    type: String,
    default: '',
  },
  iconLeft: {
    type: String,
    default: '',
  },
  iconRight: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: '',
  },
  placeholderDesktop: {
    type: String,
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  selectClasses: {
    type: String,
    default: '',
  },
  error: {
    type: Object,
    default: () => {},
  },
  additionalClasses: {
    type: String,
    default: '',
  },
  disabled: {
    type: String,
    default: '',
  },
  readonly: {
    type: String,
    default: '',
  },
  hasClear: {
    type: String,
    default: 'false',
  },
  modelValue: {
    type: String,
    default: undefined,
  },
  defaultValue: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  forceFocus: {
    type: String,
    default: 'false',
  },
});

const emit = defineEmits([
  'update:inputValue',
  'update:hasFocus',
  'update:modelValue',
  'update:blurred',
  'keyDownEnter',
]);

const inputFieldRef = ref();
const inputRef = ref();
const floatRef = ref(false);
const hasLabel = ref(false);
const showPlaceholder = ref(true);
const placeholderUsed = ref('');
const hasFocus = ref(false);
const hasError = ref(false);
const inputValue = ref(props.defaultValue);

const focusHandler = (e) => {
  floatRef.value = true;
  showPlaceholder.value = true;
  hasFocus.value = true;
  emitHandler('update:hasFocus', e);
};

const checkIfMobile = () => {
  const width = window.innerWidth;
  const isMobile = width < 768;
  if (isMobile) {
    placeholderUsed.value = props.placeholder;
  } else {
    placeholderUsed.value =
      props.placeholderDesktop !== ''
        ? props.placeholderDesktop
        : props.placeholder;
  }
};

const blurHandler = (e) => {
  const parentNode = e?.relatedTarget?.parentNode;
  const isInParent = parentNode === inputFieldRef.value;
  if (isInParent) {
    return;
  }
  const hasValue = inputRef.value?.value?.length > 0;
  hasFocus.value = false;
  emitHandler('update:blurred', e);
  if (!hasValue) {
    floatRef.value = false;
  }
  if (!hasValue && hasLabel.value) {
    showPlaceholder.value = false;
  }
};

const inputHandler = () => {
  inputValue.value = inputRef.value.value;
  emitHandler('update:inputValue', inputValue.value);
  emitHandler('update:modelValue', inputValue.value);
};

const emitHandler = (name, value) => {
  emit(name, value);
};

const clearInput = () => {
  inputRef.value.value = '';
  inputValue.value = '';
  emitHandler('update:inputValue', inputValue.value);
  emitHandler('update:modelValue', inputValue.value);
};

const clearInputnFocus = () => {
  inputRef.value.value = '';
  inputValue.value = '';
  inputRef.value.focus();
  emitHandler('update:inputValue', inputValue.value);
  emitHandler('update:modelValue', inputValue.value);
};

const focus = () => {
  inputRef.value.focus();
};

onBeforeMount(() => {
  hasLabel.value = props?.label?.length > 0;
  hasError.value = props.error?.[props.name];
  showPlaceholder.value = !props?.label?.length > 0;
  checkIfMobile();
  window.addEventListener('resize', checkIfMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkIfMobile);
});

onUpdated(() => {
  hasError.value = props.error?.[props.name];
});

const computedClasses = computed(() => {
  return {
    isFloating:
      hasFocus.value ||
      inputValue.value.length > 0 ||
      props.modelValue?.length > 0,
    hasLabel: hasLabel.value,
    hasIcon: props?.iconLeft?.length > 0,
    hasIconRight: props?.iconRight?.length > 0,
    hasFocus: hasFocus.value || props.forceFocus === 'true',
    hasError: hasError.value,
    isDisabled: props.disabled === 'true',
    [`${props.additionalClasses}`]: props.additionalClasses,
  };
});

defineExpose({
  clearInput: clearInput,
  clearInputnFocus: clearInputnFocus,
  focus,
});
</script>
